import React, { useCallback, useEffect, useState } from 'react';

import Vendor from '@components/Kpi/Parameters/Vendor';

import { Icons } from '@icons/icons';

import { FULL_PERCENT } from '@hooks/constants';
import { useQueryPresets } from '@hooks/kpis/useQueryPresets';

import { IEditPresetNameDTO, IPresetDTO, TPresetTypeProps } from '@interfaces/kpi/dto/kpiDTO';
import { IKpiTypes } from './Kpi.types';
import { IGroupSelectOptions } from '@components/common/GroupSelectKpi/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import Charts from './Charts';
import CompareSection from './CompareSection';
import Parameters from './Parameters';

import './Kpi.scss';

const Kpi = ({
	addChartToCompare,
	addChartToCorrelate,
	charts,
	clearCompareCharts,
	clearCorrelateCharts,
	compareCharts,
	compareDrillDownCharts,
	compareSettings,
	correlateCharts,
	correlateSettings,
	correlation,
	createPreset,
	deletePreset,
	drillDownCharts,
	exportCharts,
	exportChartsFromParams,
	exportCompareCharts,
	exportCorrelateCharts,
	getCharts,
	getCorrelation,
	getDrillDownCharts,
	getDrillDownCompareCharts,
	isLoadingCharts,
	isLoadingCorrelation,
	isLoadingDrillDownCharts,
	isLoadingDrillDownCompareCharts,
	isLoadingOptions,
	isLoadingPresetForm,
	isWithInterval,
	options4GFddLimenet,
	options4GFddNokia,
	options4GTddLimenet,
	options4GTddNokia,
	options5GOran,
	options5GLimenet,
	options5GNokia,
	optionsLimenetKpi,
	optionsNokiaKpi,
	optionsVendorsKpi,
	setDefaultKpiVendor,
	setIsWithInterval,
	title,
	updatePresetName,
	updatePreset,
	errorTextKpi,
	runProgress,
	progress,
	vendor,
	setVendor,
	isOranVendor,
	isLimenetVendor,
}: IKpiTypes) => {
	const [isOpenRenamePresetForm, setIsOpenRenamePresetForm] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const [isLoadingSetPreset, setIsLoadingSetPreset] = useState(false);

	const [presetSaveDate, setPresetSaveDate] = useState<IPresetDTO>();
	const [renamablePreset, setRenamablePreset] = useState<IEditPresetNameDTO>();
	const [selectedPreset, setSelectedPreset] = useState<IPresetDTO>();
	const [presetValue, setPresetValue] = useState<TSelectOptionType<string>>();

	const { presets, isLoadingPreset: isLoadingPresetOptions } = useQueryPresets({ isKpiView: true, vendor });

	const isShowPercentsSpinner = !!(
		(isOranVendor || isLimenetVendor) &&
		progress &&
		progress?.percentsProgress !== FULL_PERCENT
	);

	const isLoadingPreset = (
		isLoadingPresetForm ||
		isLoadingOptions ||
		isLoadingSetPreset ||
		isLoadingCharts ||
		isLoadingPresetOptions ||
		isShowPercentsSpinner
	);
	const isLoadingParameters = (
		isLoadingOptions ||
		isLoadingCharts ||
		isShowPercentsSpinner
	);

	useEffect(() => {
		if (!isDirty || isOranVendor) {
			return;
		}

		setPresetValue(null);
	}, [isDirty, isOranVendor]);

	useEffect(() => {
		if (!optionsVendorsKpi.length) {
			return;
		}

		if (!vendor) {
			const defaultVendor = optionsVendorsKpi.find(vendor => vendor.isDefault)?.value;
			const firstVendor = optionsVendorsKpi[0].value;

			setVendor(defaultVendor || firstVendor || '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionsVendorsKpi, optionsVendorsKpi.length]);

	const togglePresetNameForm = useCallback(() => setIsOpenRenamePresetForm(prevState => !prevState), []);

	const openSavePresetForm = useCallback((presetDate: IPresetDTO) => {
		setPresetSaveDate(presetDate);
		togglePresetNameForm();
	}, [togglePresetNameForm]);

	const closePresetForm = useCallback(() => {
		setPresetSaveDate(undefined);
		setRenamablePreset(undefined);
		togglePresetNameForm();
	}, [togglePresetNameForm]);

	const clearPreset = useCallback(() => {
		setSelectedPreset(undefined);
	}, []);

	return (
		<div className='f5gKpiContainer'>
			<CompareSection
				clearCompareCharts={clearCompareCharts}
				clearCorrelateCharts={clearCorrelateCharts}
				compareCharts={compareCharts}
				compareDrillDownCharts={compareDrillDownCharts}
				compareSettings={compareSettings}
				correlateCharts={correlateCharts}
				correlation={correlation}
				exportCompareCharts={exportCompareCharts}
				exportCorrelateCharts={exportCorrelateCharts}
				getCorrelation={getCorrelation}
				getDrillDownCompareCharts={getDrillDownCompareCharts}
				isLoadingCorrelation={isLoadingCorrelation}
				isLoadingDrillDownCompareCharts={isLoadingDrillDownCompareCharts}
			/>
			<div className='f5gKpiHeader'>
				<div className='f5gKpiPageTitle'>
					<div className='f5gKpiIcon'>{Icons.kpiIcon()}</div>
					<div className='f5gKpiTitle'>{title}</div>
				</div>
				<Vendor
					optionsVendorsKpi={optionsVendorsKpi}
					setDefaultKpiVendor={setDefaultKpiVendor}
					setVendor={setVendor}
					vendor={vendor}
				/>
			</div>
			<div className='f5gKpiContent'>
				<Parameters
					clearPreset={clearPreset}
					closePresetNameForm={closePresetForm}
					createPreset={createPreset}
					deletePreset={deletePreset}
					exportChartsFromParams={exportChartsFromParams}
					getCharts={getCharts}
					initialVendor={vendor}
					isLoadingOptions={isLoadingParameters}
					isLoadingPresetForm={isLoadingPreset}
					isLoadingSetPreset={isLoadingSetPreset}
					isOpenRenamePresetForm={isOpenRenamePresetForm}
					isPresetModifiedByUser={isDirty && !!presetValue}
					openPresetForm={openSavePresetForm}
					options4GFddLimenet={options4GFddLimenet}
					options4GFddNokia={options4GFddNokia}
					options4GTddLimenet={options4GTddLimenet}
					options4GTddNokia={options4GTddNokia}
					options5GLimenet={options5GLimenet}
					options5GNokia={options5GNokia}
					options5GOran={options5GOran}
					optionsLimenetKpi={optionsLimenetKpi as IGroupSelectOptions[]}
					optionsNokiaKpi={optionsNokiaKpi as IGroupSelectOptions[]}
					optionsVendorsKpi={optionsVendorsKpi}
					presetSaveDate={presetSaveDate}
					presetValue={presetValue as TSelectOptionType<string, Record<string, any>>}
					presets={presets as TPresetTypeProps[]}
					renamablePreset={renamablePreset}
					runProgress={runProgress}
					selectedPreset={selectedPreset}
					setDefaultKpiVendor={setDefaultKpiVendor}
					setIsDirty={setIsDirty}
					setIsLoadingSetPreset={setIsLoadingSetPreset}
					setIsWithInterval={setIsWithInterval}
					setPresetValue={setPresetValue}
					setRenamablePreset={setRenamablePreset}
					setStateSelectedPreset={setSelectedPreset}
					setVendor={setVendor}
					togglePresetNameForm={togglePresetNameForm}
					updatePreset={updatePreset}
					updatePresetName={updatePresetName}
				/>
				<Charts
					addChartToCompare={addChartToCompare}
					addChartToCorrelate={addChartToCorrelate}
					charts={charts}
					compareCharts={compareCharts}
					compareSettings={compareSettings}
					correlateCharts={correlateCharts}
					correlateSettings={correlateSettings}
					drillDownCharts={drillDownCharts}
					errorTextKpi={errorTextKpi}
					exportCharts={exportCharts}
					getDrillDownCharts={getDrillDownCharts}
					isLoadingCharts={isLoadingCharts}
					isLoadingDrillDownCharts={isLoadingDrillDownCharts}
					isWithInterval={isWithInterval}
					progress={progress}
					vendor={vendor}
				/>
			</div>
		</div>
	);
};

export default Kpi;
